import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Table, Button, Input, Spin, Tooltip, Pagination } from 'antd';
import Icon from '@ant-design/icons';
import styles from '../../SourcesContainer/components/Sources/sources.module.sass';
import { STATUS } from '../../../redux/constants';
import { ReactComponent as back_arrow } from '../../../assets/icons/BackArrow.svg';
import { SearchOutlined, InfoCircleFilled } from '@ant-design/icons';
import VersionCheck from '../../../shared-components/VersionCheck';
import { changeTitle } from '../../../redux/actions/app';
import { useDispatch } from 'react-redux';
import { ColumnTitle } from '../../../shared-components/ColumnTitle';
import PermissionDeniedPage from '../../../shared-components/PermissionDeniedPage';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import LoadingSilentPush from '../../../shared-components/LoadingSilentPush';
import { incrementUIRequestsCounter } from '../../../redux/utils/websocket/incrementUIRequestsCounter';

const BackIcon = (props) => (
  <Icon style={{ fontSize: '16px' }} component={back_arrow} {...props} />
);

function FeedsReport(props) {
  const dispatch = useDispatch();
  const { sourcesReportStatus } = props;
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [feedSearchParam, setFeedSearchParam] = useState('');
  const [createdReportMode, setCreatedReportMode] = useState(false);
  const [isEnableSearch, setIsEnableSearch] = useState(false);
  // const isEnableSearch = useRef(false);
  const selectedTab = useRef('ip');
  const hasSelected = selectedRowKeys.length > 1;
  const islocked = useRef(false);
  let interval = useRef(null);
  const currentPage = useRef(1);

  let intervalName;

  useEffect(() => {
    props.clearSourcesReports();
    clearInterval(interval);
    setSelectedRowKeys([]);
    setFeedSearchParam('');
    islocked.current = false;
    setIsEnableSearch(false);

    islocked.current === false;
    document.title = 'Feeds Report - Silent Push';
    dispatch(changeTitle('Feeds Report', null));
    props.loadMySubscriptionPlan();
    const queryParams = new URLSearchParams(window.location.search);
    const sources_uuids = queryParams.get('sources_uuids');
    if (sources_uuids) {
      setCreatedReportMode(true);
      props.loadSourcesReports('', sources_uuids);
    } else {
      setCreatedReportMode(false);
      let feed_type = queryParams.get('feed_type');

      if (!feed_type) {
        feed_type = 'ip';
      }

      selectedTab.current = feed_type;
      // props.history.push('/feeds_report?feed_type=' + feed_type);
      props.loadSourcesReports(feed_type, '');
    }

    return () => {
      clearInterval(intervalName);
      props.clearSourcesReports();
      selectedTab.current = 'ip';
      setSelectedRowKeys([]);
      setFeedSearchParam('');
      islocked.current = false;
      setIsEnableSearch(false);
    };
  }, []);

  useEffect(() => {
    setDataSource(props?.sourcesReport);

    if (
      props?.sourcesReport !== undefined &&
      props?.sourcesReport?.length > 0 &&
      props.mySubscriptionPlanStatus === STATUS.LOADED &&
      props.mySubscriptionPlan?.has_access_to_sources
    ) {
      islocked.current = true;
      let sources_uuids = populateSourcesUUIDs();
      let i = 0;
      incrementUIRequestsCounter();
      intervalName = setInterval(() => {
        if (i < props?.sourcesReport?.length) {
          props.loadSourceReport(props?.sourcesReport[i]?.uuid, sources_uuids);
          i++;
        } else if (i == props?.sourcesReport?.length) {
          setIsEnableSearch(true);
        }
      }, 1000);
    }
    return () => clearInterval(intervalName);
  }, [props?.sourcesReport, props?.mySubscriptionPlanStatus]);

  useEffect(() => {
    if (
      props?.sourcesReportWithOverlapOriginator?.length > 0 &&
      !feedSearchParam
    ) {
      setDataSource(props?.sourcesReportWithOverlapOriginator);
    }
  }, [props?.sourcesReportWithOverlapOriginator]);

  const populateSourcesUUIDs = () => {
    let sources_uuids = '';
    for (let source of props?.sourcesReport)
      sources_uuids += source['uuid'] + ',';
    return sources_uuids;
  };

  const setIPSelectedTab = () => {
    clearInterval(interval);
    selectedTab.current = 'ip';
    props.loadSourcesReports('ip', '');
    setSelectedRowKeys([]);
    setFeedSearchParam('');
    islocked.current = false;
    setIsEnableSearch(false);
    // isEnableSearch.current = false;
  };

  const setDomainSelectedTab = () => {
    clearInterval(interval);
    selectedTab.current = 'domain';
    props.loadSourcesReports('domain', '');
    setSelectedRowKeys([]);
    setFeedSearchParam('');
    islocked.current = false;
    setIsEnableSearch(false);
    // isEnableSearch.current = false;
  };

  const setURLSelectedTab = () => {
    clearInterval(interval);
    selectedTab.current = 'url';
    props.loadSourcesReports('url', '');
    setSelectedRowKeys([]);
    setFeedSearchParam('');
    islocked.current = false;
    setIsEnableSearch(false);
    // isEnableSearch.current = false;
  };

  const columns = [
    {
      title: (
        <ColumnTitle
          columnName="Feed Name"
          tooltipText="The name of the feed providing the observable"
        />
      ),
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      width: 500,
      padding: 10,
      style: { padding: 10 },
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => <span>{record?.name}</span>,
    },
    {
      title: (
        <ColumnTitle columnName="IOC Type" tooltipText="The type of the feed" />
      ),
      dataIndex: 'type',
      key: 'type',
      align: 'left',
      render: (text, record) => <span>{record?.type}</span>,
    },
    {
      title: (
        <ColumnTitle
          columnName="Vendor"
          tooltipText="The name of the owner of the feed"
        />
      ),
      dataIndex: 'vendor',
      key: 'vendor',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => <span>{record?.vendor_name}</span>,
    },
    {
      title: (
        <ColumnTitle
          columnName="Frequency (hours)"
          tooltipText="The interval between updates when the next update will happen"
        />
      ),
      dataIndex: 'frequency',
      key: 'frequency',
      sorter: (a, b) => a.frequency - b.frequency,
      render: (text, record) => <span>{record?.frequency}</span>,
    },
    {
      title: (
        <ColumnTitle
          columnName="Accuracy"
          tooltipText="Feed accuracy is based on user feedback regarding the false positives from the threat intelligence feed"
        />
      ),
      dataIndex: 'acccuracy',
      key: 'accuracy_score',
      sorter: (a, b) => a.accuracy_score - b.accuracy_score,
      render: (text, record) => <span>{record?.accuracy_score}</span>,
    },
    {
      title: (
        <ColumnTitle
          columnName="False Positive Ratio"
          tooltipText="The ratio of the number of false positives with the number of IoCs reported in the feed in the last 30 days"
        />
      ),
      dataIndex: 'false_positive_ratio',
      key: 'false_positive_ratio',
      sorter: (a, b) => a.false_positive_ratio - b.false_positive_ratio,
      render: (text, record) => <span>{record?.false_positive_ratio}</span>,
    },
    {
      title: (
        <ColumnTitle
          columnName="Overlap"
          tooltipText="The percentage of the feed's observables that are provided by other feeds/collections"
        />
      ),
      dataIndex: 'overlap_score',
      key: 'overlap_score',
      sorter: (a, b) => a.overlap_score - b.overlap_score,
      render: (text, record) => getOverlapCell(text, record),
    },
    {
      title: (
        <ColumnTitle
          columnName="Originator"
          tooltipText="The percentage of firstly reported observables since the feed/collection was added"
        />
      ),
      dataIndex: 'originator_score',
      key: 'originator_score',
      sorter: (a, b) => a.originator_score - b.originator_score,
      render: (text, record) => getOriginatorCell(text, record),
    },
  ];

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  function handleBackClick() {
    if (createdReportMode) {
      props.history.push('/feeds_report?feed_type=' + selectedTab.current);
      props.loadSourcesReports(selectedTab.current, '');
      setCreatedReportMode(false);
      islocked.current = false;
    } else {
      props.history.push('/');
    }
  }

  const createReport = () => {
    clearInterval(interval);
    let query_params = '';
    for (const index in selectedRowKeys) {
      query_params += selectedRowKeys[index] + ',';
    }
    query_params = query_params.slice(0, -1);
    props.history.push('/feeds_report?sources_uuids=' + query_params);
    props.loadSourcesReports('', query_params);
    setSelectedRowKeys([]);
    setFeedSearchParam('');
    setCreatedReportMode(true);
    islocked.current = false;
  };

  const handleNameFilterReset = () => {
    setDataSource(props?.sourcesReportWithOverlapOriginator);
    // setDataSource(props?.sourcesReport);
    setFeedSearchParam('');
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const getRowSelection = () => {
    if (createdReportMode) {
      return false;
    }
    return rowSelection;
  };

  const getOverlapCell = (text, record) => {
    return text === null ? (
      <Spin spinning={text === null} size="small"></Spin>
    ) : (
      <span>
        <Tooltip
          title={
            <>
              <div>
                Percentage of items overlapping with other Feeds/Collections
                over the last 10 days
              </div>
              <div>Total IOC: {record?.overlap_explain?.total_ioc}</div>
              <div>Total Overlap: {record?.overlap_explain?.total_overlap}</div>
            </>
          }
        >
          {text} % <InfoCircleFilled />
        </Tooltip>
      </span>
    );
  };

  const getOriginatorCell = (text, record) => {
    return text === null ? (
      <Spin spinning={text === null} size="small"></Spin>
    ) : (
      <span>
        <Tooltip
          title={
            <>
              <div>
                Percentage of firstly reported items since the Feed/Collection
                was added
              </div>
              <div>Total IOC: {record?.originator_explain?.total_ioc}</div>
              <div>
                Total Originator: {record?.originator_explain?.total_originator}
              </div>
            </>
          }
        >
          {text} % <InfoCircleFilled />
        </Tooltip>
      </span>
    );
  };

  const handleSearch = (feedValue) => {
    const filteredData = props.sourcesReport?.filter((entry) =>
      entry?.name.toString().toLowerCase().includes(feedValue.toLowerCase()),
    );
    // const filteredData = props.sourcesReportWithOverlapOriginator?.filter((entry) =>
    //   entry?.name.toString().toLowerCase().includes(feedValue.toLowerCase()),
    // );
    setDataSource(filteredData);
    setFeedSearchParam(feedValue);
  };

  return props.mySubscriptionPlanStatus === STATUS.LOADED ? (
    props.mySubscriptionPlan?.has_access_to_sources ? (
      <div className={styles.sources}>
        <div className="threatRankingBackButton" hidden={!createdReportMode}>
          <span className={styles.back_button} onClick={handleBackClick}>
            <BackIcon
              style={{
                position: 'relative',
                top: '2px',
                marginRight: '6px',
                fontSize: '16px',
              }}
            />
            <span>Back</span>
          </span>
        </div>

        <div className={styles.sourcesTableContainer}>
          <div className={styles.sourcesTableHeader} hidden={createdReportMode}>
            <Tooltip placement={'bottomLeft'} title={'Press Enter to search'}>
              <Input
                className={cx(styles.sourceNameInput, 'me-1')}
                //disabled={!isEnableSearch}
                onChange={(e) => {
                  const feedValue = e?.target?.value;
                  setFeedSearchParam(feedValue);
                  if (!feedValue) {
                    handleNameFilterReset();
                  }
                }}
                onPressEnter={(e) => {
                  const feedValue = e?.target?.value;
                  if (!feedValue) {
                    handleNameFilterReset();
                  } else {
                    handleSearch(feedValue);
                  }
                }}
                allowClear
                size="default"
                placeholder="Search Feed name"
                value={feedSearchParam}
                prefix={
                  <SearchOutlined
                    style={{ color: 'rgba(255, 255, 255, 0.3)' }}
                  />
                }
              />
            </Tooltip>

            <div className={styles.actions} style={{ width: '100%' }}>
              <div>
                <Link to={'/feeds_report?feed_type=ip'}>
                  <Button
                    className={cx(styles.scopeButton, {
                      [styles.activeScopeButton]: selectedTab.current === 'ip',
                    })}
                    type="ghost"
                    onClick={setIPSelectedTab}
                  >
                    IP
                  </Button>
                </Link>

                <Link to={'/feeds_report?feed_type=domain'}>
                  <Button
                    className={cx(styles.scopeButton, {
                      [styles.activeScopeButton]:
                        selectedTab.current === 'domain',
                    })}
                    type="ghost"
                    onClick={setDomainSelectedTab}
                  >
                    Domain
                  </Button>
                </Link>

                <Link to={'/feeds_report?feed_type=url'}>
                  <Button
                    className={cx(styles.scopeButton, {
                      [styles.activeScopeButton]: selectedTab.current === 'url',
                    })}
                    type="ghost"
                    onClick={setURLSelectedTab}
                  >
                    URL
                  </Button>
                </Link>
              </div>

              <div className={styles.createButton}>
                <Tooltip
                  placement="topLeft"
                  title="Select at least two feeds to create report"
                >
                  <Button
                    type="primary"
                    disabled={!hasSelected}
                    onClick={createReport}
                  >
                    Compare
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>

          <Fragment>
            {sourcesReportStatus === STATUS.LOADED ? (
              <Table
                className={styles.sourcesTable}
                rowKey={(record) => record?.uuid}
                columns={columns}
                dataSource={dataSource}
                rowSelection={getRowSelection()}
                pagination={{
                  showSizeChanger: false,
                  position: ['topRight', 'bottomRight'],
                  total: dataSource?.length,
                  current: currentPage.current,
                  pageSize: 20,
                  className: styles.feedsTablePagination,
                  onChange: (current, pageSize) => {
                    currentPage.current = current;
                    window.scrollTo(0, 0);
                  },
                }}
              />
            ) : (
              <LoadingSilentPush size={80} positionCenter />
            )}
          </Fragment>
        </div>
        <VersionCheck parentComponentStatus={props.sourcesReportStatus} />
      </div>
    ) : (
      <PermissionDeniedPage />
    )
  ) : (
    <LoadingSilentPush size={80} positionCenter />
  );
}

export default FeedsReport;
